/**
 * Gmail Modify Integration Hook
 *
 * Hook for managing gmail.modify scope in email agents.
 * This scope is required for marking emails as read and adding labels after auto-reply.
 */

import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { hasGoogleScope } from "@/lib/integrations/oauth";
import { openOAuthPopup } from "@/lib/oauth/popup";
import { useTranslations } from "next-intl"


export interface InboxModifyInfo {
  id: string;
  email: string;
  hasGmailModifyScope: boolean;
}

export interface LabelConfig {
  name: string;
  backgroundColor: string;
  textColor: string;
}

export interface LabelsConfig {
  incoming: LabelConfig | null;
  outgoing: LabelConfig | null;
}

export interface UseGmailModifyIntegrationOptions {
  agentId: string;
  autoLoad?: boolean;
}

export interface UseGmailModifyIntegrationReturn {
  markAsReadEnabled: boolean;
  labels: LabelsConfig;
  inboxes: InboxModifyInfo[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  loadConfig: () => Promise<void>;
  updateMarkAsReadEnabled: (enabled: boolean) => Promise<void>;
  updateLabels: (labels: LabelsConfig) => Promise<void>;
  connectGmailModify: (inboxId: string) => Promise<void>;
}

export function useGmailModifyIntegration(
  options: UseGmailModifyIntegrationOptions
): UseGmailModifyIntegrationReturn {
  const tUi = useTranslations("Feedback.UseModifyIntegration")

  const { agentId, autoLoad = true } = options;
  const { fetchWithAuth } = useAuth();

  const [markAsReadEnabled, setMarkAsReadEnabled] = useState(false);
  const [labels, setLabels] = useState<LabelsConfig>({ incoming: null, outgoing: null });
  const [inboxes, setInboxes] = useState<InboxModifyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    if (!agentId) return;

    setIsLoading(true);
    setError(null);

    try {
      const configRes = await fetchWithAuth(`/api/agents/email/${agentId}/config`);
      if (!configRes.ok) {
        const data = await configRes.json();
        throw new Error(data.error || "Failed to load agent configuration");
      }
      const configData = await configRes.json();
      setMarkAsReadEnabled(configData.config?.auto_reply?.mark_as_read_on_reply ?? false);

      const labelsConfig = configData.config?.auto_reply?.labels;
      setLabels({
        incoming: labelsConfig?.incoming ?? null,
        outgoing: labelsConfig?.outgoing ?? null,
      });

      const inboxesRes = await fetchWithAuth(`/api/agents/email/${agentId}/inboxes`);
      if (!inboxesRes.ok) {
        const data = await inboxesRes.json();
        throw new Error(data.error || "Failed to load inboxes");
      }
      const inboxesData = await inboxesRes.json();

      const inboxList: InboxModifyInfo[] = (inboxesData.inboxes || []).map((inbox: any) => {
        const scopes = inbox.oauth_tokens?.scopes || [];
        return {
          id: inbox.id,
          email: inbox.address,
          hasGmailModifyScope: hasGoogleScope(scopes, "gmail_modify"),
        };
      });

      setInboxes(inboxList);
    } catch (err: any) {
      console.error("Failed to load gmail modify config:", err);
      setError(err.message || "Failed to load configuration");
    } finally {
      setIsLoading(false);
    }
  }, [agentId, fetchWithAuth]);

  const updateMarkAsReadEnabled = useCallback(
    async (enabled: boolean) => {
      if (!agentId) return;

      setIsSaving(true);
      setError(null);

      try {
        const res = await fetchWithAuth(`/api/agents/email/${agentId}/config`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            config: { auto_reply: { mark_as_read_on_reply: enabled } },
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || "Failed to save configuration");
        }

        setMarkAsReadEnabled(enabled);
      } catch (err: any) {
        console.error("Failed to update mark_as_read_on_reply:", err);
        setError(err.message || "Failed to save configuration");
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [agentId, fetchWithAuth]
  );

  const updateLabels = useCallback(
    async (newLabels: LabelsConfig) => {
      if (!agentId) return;

      setIsSaving(true);
      setError(null);

      try {
        const res = await fetchWithAuth(`/api/agents/email/${agentId}/config`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            config: { auto_reply: { labels: newLabels } },
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || "Failed to save configuration");
        }

        setLabels(newLabels);
      } catch (err: any) {
        console.error("Failed to update labels:", err);
        setError(err.message || "Failed to save configuration");
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [agentId, fetchWithAuth]
  );

  const connectGmailModify = useCallback(
    async (targetInboxId: string) => {
      if (!targetInboxId) {
        setError(tUi("noInboxSpecifiedPleaseSelectAnInboxTo"));
        return;
      }

      setError(null);

      try {
        const res = await fetchWithAuth("/api/integrations/google/oauth/scope", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            inbox_id: targetInboxId,
            scope_type: "gmail_modify",
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || "Failed to initiate Gmail Modify connection");
        }

        const data = await res.json();

        if (data.auth_url) {
          openOAuthPopup({
            url: data.auth_url,
            name: "gmail_modify_oauth",
            onMessage: () => loadConfig(),
            onClose: () => loadConfig(),
            errorKey: "gmail_modify_oauth_error",
            onError: (err) => setError(`Gmail Modify connection failed: ${err}`),
          });
        }
      } catch (err: any) {
        console.error("Failed to connect Gmail Modify:", err);
        setError(err.message || "Failed to connect Gmail Modify");
      }
    },
    [fetchWithAuth, loadConfig]
  );

  useEffect(() => {
    if (autoLoad && agentId) {
      loadConfig();
    }
  }, [autoLoad, agentId, loadConfig]);

  return {
    markAsReadEnabled,
    labels,
    inboxes,
    isLoading,
    isSaving,
    error,
    loadConfig,
    updateMarkAsReadEnabled,
    updateLabels,
    connectGmailModify,
  };
}
